/**
 * Ajustes → Operaciones (WP-01A). Hoy: el estado de la copia nocturna.
 *
 * La copia la hace el servidor por su cuenta (scripts/backup-verify.sh, cron);
 * esta pantalla solo LEE el estado que deja y lo explica: cuándo fue la última
 * buena, si se probó a restaurar, si va cifrada y si ha salido del servidor.
 * Lo que esté mal llega además como notificación a los ADMIN.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  CloudUpload,
  DatabaseBackup,
  HardDriveDownload,
  Loader2,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { formatDateTime, formatNumber, formatRelative } from '../../lib/formatting'

type Health = 'ok' | 'warning' | 'stale' | 'failed' | 'missing' | 'unconfigured'

interface BackupRun {
  host?: string | null
  startedAt: string
  finishedAt: string
  result: 'ok' | 'warn' | 'fail'
  step?: string | null
  message?: string | null
  warnings?: string | null
  dumpBytes?: number | null
  dumpSha256?: string | null
  archiveFile?: string | null
  archiveBytes?: number | null
  archiveSha256?: string | null
  encrypted?: boolean
  offsiteMode?: 'none' | 'rclone' | 'pull' | null
  offsiteTarget?: string | null
  offsiteUploaded?: boolean
  restoreImage?: string | null
  restoreTables?: number | null
  restoreRows?: number | null
  restoreUsers?: number | null
  restoreInvoices?: number | null
  lastMigration?: string | null
  minio?: string | null
}

interface BackupPull {
  client?: string | null
  checkedAt: string
  result: 'ok' | 'fail'
  message?: string | null
  pulledAt?: string | null
  newestArchive?: string | null
  newestCreatedAt?: string | null
  localCopies?: number | null
}

interface BackupStatus {
  configured: boolean
  health: Health
  checkedAt: string
  maxAgeHours: number
  pullMaxAgeHours: number
  lastRun: BackupRun | null
  lastOk: BackupRun | null
  lastOkAgeHours: number | null
  lastPull: BackupPull | null
  lastPullAgeHours: number | null
  problems: { code: string; detail: string | null }[]
}

const HEALTH_STYLE: Record<Health, { box: string; icon: typeof CheckCircle2 }> = {
  ok: { box: 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40', icon: CheckCircle2 },
  warning: { box: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/40', icon: AlertTriangle },
  missing: { box: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/40', icon: AlertTriangle },
  stale: { box: 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30', icon: XCircle },
  failed: { box: 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30', icon: XCircle },
  unconfigured: { box: 't-secondary border-base', icon: AlertCircle },
}

function formatBytes(n: number | null | undefined): string {
  if (n == null) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = n
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i += 1
  }
  return `${formatNumber(v, { maximumFractionDigits: i === 0 ? 0 : 1 })} ${units[i]}`
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 py-1.5">
      <dt className="text-xs t-muted sm:w-48 shrink-0">{label}</dt>
      <dd className="text-sm t-primary min-w-0 break-words">{children}</dd>
    </div>
  )
}

export default function OperationsPage() {
  const { t } = useTranslation('settings')
  const [status, setStatus] = useState<BackupStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setStatus(await api.get<BackupStatus>('/ops/backup-status'))
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('operations.errorLoad')))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const when = (iso: string | null | undefined) =>
    iso ? `${formatDateTime(iso, { dateStyle: 'short', timeStyle: 'short' })} (${formatRelative(iso)})` : '—'

  const health = status?.health ?? 'unconfigured'
  const style = HEALTH_STYLE[health]
  const HealthIcon = style.icon
  const ok = status?.lastOk ?? null
  const run = status?.lastRun ?? null
  const pull = status?.lastPull ?? null
  const mode = ok?.offsiteMode ?? run?.offsiteMode ?? null

  return (
    <div className="space-y-4 max-w-3xl">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">{t('operations.title')}</h2>
          <p className="text-sm t-muted mt-1">{t('operations.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5 disabled:opacity-50 whitespace-nowrap"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {t('operations.refresh')}
        </button>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading && !status ? (
        <div className="t-muted flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t('operations.loading')}
        </div>
      ) : status ? (
        <section className="surface p-4 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-jr2-500/15 p-2">
              <DatabaseBackup size={18} className="text-jr2-700 dark:text-jr2-200" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="t-primary font-semibold">{t('operations.backup.title')}</h3>
              <p className="text-xs t-muted mt-0.5">
                {t('operations.backup.subtitle', { max: status.maxAgeHours })}
              </p>
            </div>
          </div>

          <div className={`p-3 text-sm rounded-lg border flex items-start gap-2 ${style.box}`}>
            <HealthIcon size={16} className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold">{t(`operations.health.${health}`)}</div>
              {status.problems.length > 0 && (
                <ul className="mt-1 space-y-1">
                  {status.problems.map((p, i) => (
                    <li key={`${p.code}-${i}`} className="text-xs">
                      {t(`operations.problem.${p.code}`, {
                        max: status.maxAgeHours,
                        pullMax: status.pullMaxAgeHours,
                        hours: status.lastOkAgeHours ?? '—',
                        pullHours: status.lastPullAgeHours ?? '—',
                      })}
                      {p.detail ? <span className="block opacity-80 break-words">{p.detail}</span> : null}
                    </li>
                  ))}
                </ul>
              )}
              {!status.configured && (
                <p className="text-xs mt-1">
                  {t('operations.unconfiguredPre')}{' '}
                  <code className="px-1 rounded bg-[var(--bg-input)]">BACKUP_STATUS_DIR</code>{' '}
                  {t('operations.unconfiguredPost')}
                </p>
              )}
            </div>
          </div>

          {status.configured && (
            <>
              <div>
                <h4 className="text-xs font-semibold t-secondary uppercase tracking-wide flex items-center gap-1.5">
                  <ShieldCheck size={12} /> {t('operations.backup.lastOk')}
                </h4>
                {ok ? (
                  <dl className="mt-1 divide-y divide-slate-700/20">
                    <Row label={t('operations.field.finishedAt')}>{when(ok.finishedAt)}</Row>
                    <Row label={t('operations.field.result')}>
                      {t(`operations.result.${ok.result}`)}
                      {ok.host ? <span className="t-muted"> · {ok.host}</span> : null}
                    </Row>
                    <Row label={t('operations.field.restoreTest')}>
                      {t('operations.restoreSummary', {
                        tables: ok.restoreTables ?? '—',
                        rows: ok.restoreRows != null ? formatNumber(ok.restoreRows) : '—',
                        users: ok.restoreUsers ?? '—',
                        invoices: ok.restoreInvoices != null ? formatNumber(ok.restoreInvoices) : '—',
                      })}
                      {ok.restoreImage ? <span className="block text-xs t-muted">{ok.restoreImage}</span> : null}
                    </Row>
                    <Row label={t('operations.field.migration')}>
                      <code className="text-xs">{ok.lastMigration ?? '—'}</code>
                    </Row>
                    <Row label={t('operations.field.dump')}>{formatBytes(ok.dumpBytes)}</Row>
                    <Row label={t('operations.field.archive')}>
                      {ok.archiveFile ? (
                        <>
                          {ok.archiveFile} · {formatBytes(ok.archiveBytes)} ·{' '}
                          {ok.encrypted ? t('operations.encrypted') : t('operations.notEncrypted')}
                          {ok.archiveSha256 ? (
                            <span className="block text-xs t-muted font-mono" title={ok.archiveSha256}>
                              sha256 {ok.archiveSha256.slice(0, 16)}…
                            </span>
                          ) : null}
                        </>
                      ) : (
                        t('operations.noArchive')
                      )}
                    </Row>
                    <Row label={t('operations.field.minio')}>{t(`operations.minio.${ok.minio ?? 'skipped'}`)}</Row>
                  </dl>
                ) : (
                  <p className="text-sm t-muted mt-1">{t('operations.none')}</p>
                )}
              </div>

              {run && run.result === 'fail' && (
                <div>
                  <h4 className="text-xs font-semibold t-secondary uppercase tracking-wide flex items-center gap-1.5">
                    <XCircle size={12} /> {t('operations.backup.lastRunFailed')}
                  </h4>
                  <dl className="mt-1 divide-y divide-slate-700/20">
                    <Row label={t('operations.field.finishedAt')}>{when(run.finishedAt)}</Row>
                    <Row label={t('operations.field.step')}>{run.step ?? '—'}</Row>
                    <Row label={t('operations.field.message')}>{run.message ?? '—'}</Row>
                  </dl>
                </div>
              )}

              <div>
                <h4 className="text-xs font-semibold t-secondary uppercase tracking-wide flex items-center gap-1.5">
                  {mode === 'pull' ? <HardDriveDownload size={12} /> : <CloudUpload size={12} />}{' '}
                  {t('operations.backup.offsite')}
                </h4>
                <dl className="mt-1 divide-y divide-slate-700/20">
                  <Row label={t('operations.field.mode')}>{t(`operations.mode.${mode ?? 'none'}`)}</Row>
                  {mode === 'rclone' && (
                    <Row label={t('operations.field.target')}>
                      {ok?.offsiteTarget ?? '—'} ·{' '}
                      {ok?.offsiteUploaded ? t('operations.uploaded') : t('operations.notUploaded')}
                    </Row>
                  )}
                  {mode === 'pull' && (
                    <>
                      <Row label={t('operations.field.pullChecked')}>
                        {pull ? (
                          <>
                            {when(pull.checkedAt)}
                            {pull.client ? <span className="t-muted"> · {pull.client}</span> : null}
                          </>
                        ) : (
                          t('operations.never')
                        )}
                      </Row>
                      <Row label={t('operations.field.pullNewest')}>
                        {pull?.newestArchive ? (
                          <>
                            {pull.newestArchive}
                            <span className="block text-xs t-muted">{when(pull.newestCreatedAt)}</span>
                          </>
                        ) : (
                          '—'
                        )}
                      </Row>
                      <Row label={t('operations.field.pullCopies')}>{pull?.localCopies ?? '—'}</Row>
                    </>
                  )}
                </dl>
              </div>
            </>
          )}

          <p className="text-xs t-faint border-t border-base pt-3">{t('operations.docsHint')}</p>
        </section>
      ) : null}
    </div>
  )
}
